import { Box, Chip, Theme, Typography, useMediaQuery } from "@mui/material";
import CustomTable, { IColumn } from "../../common/CustomTable/CustomTable";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  getLoginActivity,
  getLoginActivityByFilter,
} from "./loginActivityActions";
import LoginRecords from "./LoginRecords/LoginRecords";
import StarIcon from "@mui/icons-material/Star";
import FiltersSection from "./LoginRecords/FiltersSection";
import { ILoginActivity } from "../../models/admin/loginActivity/loginActivity";
import { RootState } from "../../store/store";

export interface FilterState {
  activityType: string[];
  userType: string[];
  deviceType: string[];
  browserType: string[];
  quickFilter?: string;
  startDate?: string;
  endDate?: string;
}

const formatDate = (dateString: string): string => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date
    .toLocaleString("en-GB", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    })
    .replace(/,/g, "");
};

const LoginActivity = () => {
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery("(max-width:767px)");
  const [loginData, setLoginData] = useState<ILoginActivity[]>([]);
  const [filters, setFilters] = useState<FilterState>({
    activityType: [],
    userType: [],
    deviceType: [],
    browserType: [],
    quickFilter: "",
    startDate: "",
    endDate: "",
  });

  const { loginActivity } = useAppSelector((state) => state.loginActivity);
  const { calledReducerType, isLoading } = useAppSelector(
    (store: RootState) => store.common
  );

  useEffect(() => {
    dispatch(getLoginActivity());
  }, [dispatch]);

  useEffect(() => {
    if (loginActivity) {
      setLoginData(loginActivity);
    }
  }, [loginActivity]);

  const handleFilterClick = (filterType: keyof FilterState, value: string) => {
    setFilters((prevFilters) => {
      const currentValues = Array.isArray(prevFilters[filterType])
        ? (prevFilters[filterType] as string[])
        : [];
      const newValues =
        filterType === "quickFilter"
          ? prevFilters.quickFilter === value
            ? ""
            : value
          : currentValues.includes(value)
          ? currentValues.filter((item) => item !== value)
          : [...currentValues, value];
      const newFilters = { ...prevFilters, [filterType]: newValues };
      dispatch(getLoginActivityByFilter(newFilters));
      return newFilters;
    });
  };

  const handleDateChange = (type: "start" | "end", value: string) => {
    setFilters((prevFilters) => {
      const newFilters = {
        ...prevFilters,
        [type === "start" ? "startDate" : "endDate"]: value,
      };
      dispatch(getLoginActivityByFilter(newFilters));
      return newFilters;
    });
  };

  const handleExportToCSV = () => {
    const headers = [
      "Email/Phone Number",
      "User Type",
      "Comments",
      "Login Time",
      "Logout Time",
      "Device",
      "IP Address",
    ];
    const csvRows = [headers.join(",")];

    loginData.forEach(
      ({
        email,
        userType,
        comments,
        loginTime,
        logoutTime,
        deviceType,
        ipAddress,
      }) => {
        const values = [
          email,
          userType,
          comments,
          formatDate(loginTime || ""),
          formatDate(logoutTime || ""),
          deviceType,
          ipAddress,
        ].map((val) => `"${val}"`);
        csvRows.push(values.join(","));
      }
    );

    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "login_activity.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const columns: IColumn[] = [
    {
      label: "",
      Cell: ({ activity, live }: any) => (
        <Box
          sx={{ display: "flex", alignItems: "center", textAlign: "center" }}
        >
          <Chip
            size="medium"
            label={activity && activity?.toUpperCase()}
            color={
              activity?.toLowerCase() === "success" ? "success" : "warning"
            }
          />
          {live && <StarIcon sx={{ fill: "red" }} />}
        </Box>
      ),
    },
    { label: "Email/Phone Number", accessorKey: "email" },
    { label: "User Type", accessorKey: "userType" },
    { label: "Comments", accessorKey: "comments" },
    {
      label: "Login Time",
      accessorKey: "loginTime",
      Cell: ({ loginTime }: any) => <span>{formatDate(loginTime)}</span>,
    },
    {
      label: "Logout Time",
      accessorKey: "logoutTime",
      Cell: ({ logoutTime }: any) => <span>{formatDate(logoutTime)}</span>,
    },
    { label: "Device", accessorKey: "deviceType" },
    { label: "IP Address", accessorKey: "ipAddress" },
  ];

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          paddingTop: 2,
        }}
        id="login_activity_header"
        data-test-id="login_activity_header"
        aria-label="Login Activity Header"
      >
        <Typography
          variant="h6"
          sx={{
            background: (theme: Theme) =>
              `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            fontWeight: "bold",
          }}
          id="login_activity_title"
          data-test-id="login_activity_title"
          aria-label="Login Reports"
        >
          Login Reports
        </Typography>
      </Box>
      <FiltersSection
        filters={filters}
        setFilters={setFilters}
        handleFilterClick={handleFilterClick}
        handleExportToCSV={handleExportToCSV}
        handleDateChange={handleDateChange}
      />
      <LoginRecords loginActivity={loginData} />
      <Box
        sx={{ paddingY: 3, maxWidth: isMobile ? "calc(100vw - 48px)" : "none" }}
        id="login_activity_table_container"
        data-test-id="login_activity_table_container"
        aria-label="Login Activity Table Container"
      >
        <CustomTable
          data={loginData}
          columns={columns}
          pagination
          stickyHeader
          displaySkeleton={
            isLoading && calledReducerType === "all/loginActivity"
          }
        />
      </Box>
    </>
  );
};

export default LoginActivity;
